//#region types
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { AuthEnums, ReceiptEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { ReceiptFormTypes } from "models/formTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { ReceiptUploadedFile } from "models/formTypes/receipt";
//#endregion
//#region components
import useAPIClientHelper from "./useAPIClientHelper";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useReceiptService() {
  const { apiClientInstance } = useAPIClientHelper();

  const get = (id: number, key?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetById(id)
      .then((res) => {
        const receipt = res.data;
        return {
          ...receipt,
          paymentMethods: receipt.paymentMethods?.filter((pm) => !pm.isPaymentMethodTypeDisabled),
          key: key,
        };
      });
  };
  const sign = (id: number, receipt: ApiModels.Receipt) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptClient.UpdateAndSign(id, receipt as ApiModels.ReceiptPut);
  };
  const put = (id: number, receipt: ApiModels.Receipt) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptClient.Put(id, receipt as ApiModels.ReceiptPut);
  };
  const deleteImage = (receiptId: number, imageId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptClient.DeleteImageAsync(receiptId, imageId);
  };
  const deleteRecipt = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptClient.Cancel(id);
  };

  const getPdf = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).ReceiptClient.GetPdfAsync(id);
  };

  const getAttachment = (receiptId: number, attachmentId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetAttachmentAsync(receiptId, attachmentId)
      .then((res) => res.data);
  };

  const getAttachmentData = (receiptId: number, attachmentId: number | string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetAttachmentAsync(receiptId, attachmentId as number)
      .then((res) => res.data.data ?? "");
  };

  const sendBack = (id: number, message: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptClient.SendBack(id, {
      message,
    } as ApiModels.ReceiptSendBackData);
  };

  const getTransactions = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetTransactions(id)
      .then((res) => res.data);
  };
  const getSimilarReceipts = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.FindSimilarAsync(id, 7)
      .then((res) => res.data);
  };

  const getStateLogs = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetStateLogs(id)
      .then((res) => res.data);
  };

  const reportFilter = (
    baseFilter: WintUtilsWebApi.Filter,
    formFilter: ReceiptFormTypes.Filter,
    loggedInUser: number
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company).ReceiptClient.GetByFilter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const reportToAPIFilter = (
    formFilter: ReceiptFormTypes.Filter
  ): Omit<ApiModels.ReceiptFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      serialNumberFrom: formFilter.serialNumber?.lowest,
      serialNumberTo: formFilter.serialNumber?.highest,
      states: formFilter?.viewStatuses
        ?.map((s) => ({ states: getReceiptStateByViewStatus(s.id as ReceiptEnums.ReceiptViewStatus) }))
        .flatMap((s) => s.states),
      receiptCategories: formFilter.receiptCategories?.map((u) => u.id as number),
      createdFromDate: formFilter.created?.start,
      createdToDate: formFilter.created?.end,
      amountFrom: formFilter.amount?.lowest,
      amountTo: formFilter.amount?.highest,
      uploadedByUserIds: formFilter.uploadedByUsers?.map((u) => u.id as number),
      paymentState: formFilter.paymentState,
      paymentMethods: formFilter.paymentMethod !== undefined ? [formFilter.paymentMethod] : undefined,
      paymentMethodTypes: formFilter.paymentMethodTypes?.map((u) => u.id as number),
      dimensions: formFilter.dimensions ?? [],
    };
  };

  const create = (receipt: ApiModels.ReceiptPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptClient.Post(receipt);
  };

  const getReceiptStateByViewStatus = (status: ReceiptEnums.ReceiptViewStatus) => {
    switch (status) {
      case ReceiptEnums.ReceiptViewStatus.InProcess:
        return [ApiModels.ReceiptStateApiModel.Received, ApiModels.ReceiptStateApiModel.Investigation];
      case ReceiptEnums.ReceiptViewStatus.InTheBooks:
        return [
          ApiModels.ReceiptStateApiModel.Booked,
          ApiModels.ReceiptStateApiModel.PayoutRescheduled,
          ApiModels.ReceiptStateApiModel.PaymentSign,
          ApiModels.ReceiptStateApiModel.Stopped,
        ];
      case ReceiptEnums.ReceiptViewStatus.ForFinalApproval:
        return [ApiModels.ReceiptStateApiModel.WaitingAcceptance];
      case ReceiptEnums.ReceiptViewStatus.SupplementRequested:
        return [
          ApiModels.ReceiptStateApiModel.SentBackToCustomer,
          ApiModels.ReceiptStateApiModel.SentBackFromReceiptAdmin,
        ];
      case ReceiptEnums.ReceiptViewStatus.Cancelled:
        return [ApiModels.ReceiptStateApiModel.Canceled];
      case ReceiptEnums.ReceiptViewStatus.Draft:
        return [ApiModels.ReceiptStateApiModel.Draft];

      default:
        return [];
    }
  };

  const apiToViewStatus = (status: ApiModels.ReceiptStateApiModel) => {
    switch (status) {
      case ApiModels.ReceiptStateApiModel.Received:
      case ApiModels.ReceiptStateApiModel.Investigation:
        return ReceiptEnums.ReceiptViewStatus.InProcess;
      case ApiModels.ReceiptStateApiModel.Booked:
      case ApiModels.ReceiptStateApiModel.PayoutRescheduled:
      case ApiModels.ReceiptStateApiModel.PaymentSign:
      case ApiModels.ReceiptStateApiModel.Stopped:
        return ReceiptEnums.ReceiptViewStatus.InTheBooks;
      case ApiModels.ReceiptStateApiModel.WaitingAcceptance:
        return ReceiptEnums.ReceiptViewStatus.ForFinalApproval;
      case ApiModels.ReceiptStateApiModel.SentBackToCustomer:
      case ApiModels.ReceiptStateApiModel.SentBackFromReceiptAdmin:
        return ReceiptEnums.ReceiptViewStatus.SupplementRequested;
      case ApiModels.ReceiptStateApiModel.Canceled:
        return ReceiptEnums.ReceiptViewStatus.Cancelled;
      case ApiModels.ReceiptStateApiModel.Draft:
        return ReceiptEnums.ReceiptViewStatus.Draft;
    }
  };

  const getStateText = (status?: ReceiptEnums.ReceiptViewStatus) => {
    switch (status) {
      case ReceiptEnums.ReceiptViewStatus.InProcess:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.InProcess);
      case ReceiptEnums.ReceiptViewStatus.InTheBooks:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.InTheBooks);
      case ReceiptEnums.ReceiptViewStatus.ForFinalApproval:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ForFinalApproval);
      case ReceiptEnums.ReceiptViewStatus.SupplementRequested:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.SupplementRequested);
      case ReceiptEnums.ReceiptViewStatus.Cancelled:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Cancelled);
      case ReceiptEnums.ReceiptViewStatus.Draft:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Draft);

      default:
        return " ";
    }
  };

  const getPaymentStatusText = (status: ApiModels.PaymentState) => {
    switch (status) {
      case ApiModels.PaymentState.Paid:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Paid);
      default:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Unpaid);
    }
  };

  //used in both Receipt.Todo and Receipt.Details forms
  const getUpdatedReceipt = (
    receipt: ApiModels.Receipt,
    values: any,
    isDraft?: boolean,
    attachments?: ApiModels.ReceiptAttachment[]
  ) => {
    const receiptCategoryType =
      FormFieldConstants.Receipt.Details.ReceiptCategoryType in values ? values.receiptCategoryType : undefined;
    const otherExplanation =
      FormFieldConstants.Receipt.Todo.OtherExplanation in values &&
      receiptCategoryType === ApiModels.ReceiptCategoryType.Other
        ? values.otherExplanation
        : undefined;

    return {
      ...receipt,
      receiptCategoryId:
        FormFieldConstants.Receipt.Details.ReceiptCategoryId in values
          ? values.receiptCategoryId
          : isDraft
            ? undefined
            : receipt.receiptCategoryId,
      intent: otherExplanation
        ? otherExplanation
        : FormFieldConstants.Receipt.Details.Intent in values
          ? receiptCategoryType === ApiModels.ReceiptCategoryType.Representation ||
            receiptCategoryType === ApiModels.ReceiptCategoryType.EmployeeRepresentation
            ? values.intent
            : ""
          : receipt.intent,
      persons:
        FormFieldConstants.Receipt.Details.Persons in values
          ? receiptCategoryType === ApiModels.ReceiptCategoryType.Representation ||
            receiptCategoryType === ApiModels.ReceiptCategoryType.EmployeeRepresentation
            ? values.persons.map((p: ApiModels.ReceiptPerson) => ({
                ...p,
                id: 0,
              }))
            : []
          : receipt.persons,
      amount: FormFieldConstants.Receipt.Details.Amount in values ? values.amount : receipt.amount,
      currency: FormFieldConstants.Receipt.Details.Currency in values ? values.currency : receipt.currency,
      dateTime:
        FormFieldConstants.Receipt.Details.PurchaseDate in values
          ? (values.purchaseDate ?? undefined)
          : receipt.dateTime,
      paymentMethodId:
        FormFieldConstants.Receipt.Details.PaymentMethodId in values ? values.paymentMethodId : receipt.paymentMethodId,
      dimensionList:
        FormFieldConstants.Receipt.Details.Dimensions in values ? values.dimensions : receipt.dimensionList,
      attachments:
        (FormFieldConstants.Receipt.Details.FilesToUpload in values &&
        ((values.filesToUpload as UploadedFile[])?.some((i) => i.id === undefined) ||
          (values.filesToUpload as UploadedFile[])?.length !== receipt.attachments?.length)
          ? (values.filesToUpload as ApiModels.ReceiptAttachment[])
          : undefined) ?? attachments,
      receiptPaymentInfo:
        FormFieldConstants.Receipt.Details.CanPayEmployee in values &&
        !values.canPayEmployee &&
        values.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense
          ? {
              personNumber: values.personNumber,
              clearingNumber: values.clearingNumber,
              bankAccount: values.bankAccount,
              hasMissingPaymentDetails: values.isManualPayment,
            }
          : undefined,
      comment: FormFieldConstants.Receipt.Details.InternalNote in values ? values.internalNote : receipt.comment,
      supplierName:
        FormFieldConstants.Receipt.Details.SupplierName in values ? values.supplierName : receipt.supplierName,
      isOtherPaymentMethod: values.isOtherPaymentMethod,
      otherPaymentMethodExplanation: values.otherPaymentMethodExplanation,
    };
  };

  //used in both Receipt.Todo and Receipt.Details forms
  const getUpdatedReceiptDraft = (receipt: ApiModels.Receipt, values: any) => {
    const receiptCategoryType =
      FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptCategoryType in values
        ? values.receiptCategoryType
        : undefined;
    const otherExplanation =
      FormFieldConstants.Receipt.Todo.OtherExplanation in values &&
      receiptCategoryType === ApiModels.ReceiptCategoryType.Other
        ? values.otherExplanation
        : undefined;

    return {
      ...receipt,
      receiptCategoryId:
        FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptCategoryId in values ? values.receiptCategoryId : undefined,
      amount: FormFieldConstants.Receipt.ReceiptDraftTodo.Amount in values ? values.amount : receipt.amount,
      currency: FormFieldConstants.Receipt.ReceiptDraftTodo.Currency in values ? values.currency : receipt.currency,
      paymentMethodId:
        FormFieldConstants.Receipt.ReceiptDraftTodo.PaymentMethodId in values
          ? values.paymentMethodId
          : receipt.paymentMethodId,
      dimensionList: FormFieldConstants.Receipt.ReceiptDraftTodo.Dimensions in values ? values.dimensions : [],
      attachments:
        FormFieldConstants.Receipt.ReceiptDraftTodo.FilesToUpload in values &&
        ((values.filesToUpload as ReceiptUploadedFile[])?.some((i) => i.id === undefined || i.isDocumentDraft) ||
          (values.filesToUpload as UploadedFile[])?.length !== receipt.attachments?.length)
          ? (values.filesToUpload as ApiModels.ReceiptAttachment[])
          : undefined,
      persons:
        FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptPersons in values
          ? receiptCategoryType === ApiModels.ReceiptCategoryType.Representation ||
            receiptCategoryType === ApiModels.ReceiptCategoryType.EmployeeRepresentation
            ? values.receiptPersons.map((p: ApiModels.ReceiptPerson) => ({
                ...p,
                id: 0,
              }))
            : []
          : [],
      intent: otherExplanation
        ? otherExplanation
        : FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptPurpose in values
          ? receiptCategoryType === ApiModels.ReceiptCategoryType.Representation ||
            receiptCategoryType === ApiModels.ReceiptCategoryType.EmployeeRepresentation
            ? values.receiptPurpose
            : ""
          : undefined,
    };
  };

  const getTodoState = (receiptViewState: ReceiptEnums.ReceiptViewState) => {
    switch (receiptViewState) {
      case ReceiptEnums.ReceiptViewState.ToApprove:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ToApprove);
      case ReceiptEnums.ReceiptViewState.MissingInfo:
      case ReceiptEnums.ReceiptViewState.MissingPaymentInfo:
        return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MissingInfoMessage);
      default:
        return undefined;
    }
  };

  const getTodoStateLabelClass = (receiptViewState: ReceiptEnums.ReceiptViewState) => {
    switch (receiptViewState) {
      case ReceiptEnums.ReceiptViewState.ToApprove:
        return cssExports.blueTag;
      case ReceiptEnums.ReceiptViewState.MissingInfo:
      case ReceiptEnums.ReceiptViewState.MissingPaymentInfo:
        return cssExports.yellowTag;
      default:
        return cssExports.grayTag;
    }
  };

  const handleReceiptDocumentDraft = (receiptDocumentDraft: ApiModels.ReceiptDocumentDraft) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptClient.HandleDocumentDraft(receiptDocumentDraft);
  };

  const getTopWintCardSuppliers = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetTopWintCardSuppliers()
      .then((res) => res.data);
  };

  const searchWintCardSuppliers = (value: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .ReceiptClient.GetWintCardSuppliers(value)
      .then((res) => res.data);
  };

  return {
    deleteImage,
    getPaymentStatusText,
    getStateText,
    apiToViewStatus,
    getReceiptStateByViewStatus,
    create,
    reportToAPIFilter,
    reportFilter,
    getStateLogs,
    getSimilarReceipts,
    getTransactions,
    sendBack,
    getAttachmentData,
    getAttachment,
    getPdf,
    deleteRecipt,
    put,
    sign,
    get,
    getUpdatedReceipt,
    getUpdatedReceiptDraft,
    getTodoState,
    getTodoStateLabelClass,
    handleReceiptDocumentDraft,
    getTopWintCardSuppliers,
    searchWintCardSuppliers,
  };
}

export default useReceiptService;
